import { useEffect } from 'react';
import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface HowItWorksPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

const STEPS = [
  {
    n: '01',
    label: 'Install',
    title: 'Install in under a minute',
    body: 'Salifly runs as a Chrome side panel — always one click away without switching tabs. Install from the Chrome Web Store, sign in with Google or your work email, then set your industry, role, and preferred channels. Four steps, under sixty seconds.',
    tags: ['Chrome Side Panel', 'Google OAuth', 'Email Magic Link', 'No admin approval needed'],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /><path d="M8 12l4 4 4-4" /><path d="M12 8v8" />
      </svg>
    ),
  },
  {
    n: '02',
    label: 'Analyze',
    title: 'Open any conversation — click Analyze',
    body: 'Works on Gmail, LinkedIn, Salesforce, HubSpot, Outreach, Outlook Web, and 10+ more platforms without any per-site configuration. Open any thread, click Analyze in the Salifly panel, and context capture reads the full conversation thread.',
    tags: ['Gmail', 'LinkedIn', 'Salesforce', 'HubSpot', 'Outreach', 'Outlook Web', '10+ more'],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  {
    n: '03',
    label: 'Analyze',
    title: 'Salifly reads the full deal — not just the screen',
    body: 'Most AI tools only see what\'s visible on screen. Salifly sends a dual payload: a screenshot for spatial context plus the full DOM-scraped conversation thread for every message, objection, and buying signal. Personal data is stripped client-side before any analysis runs. Three calibrated responses stream back progressively as they\'re generated — each one grounded in deal stage, objections, and your rep\'s winning plays.',
    tags: ['Dual payload', 'DOM thread scrape', 'Client-side PII masking', 'Vision + Language AI', 'Progressive streaming'],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    n: '04',
    label: 'Respond',
    title: 'Pick a response, refine it, and inject',
    body: 'Three responses appear, each calibrated to your industry playbook, the detected deal stage, and the lead\'s actual language from the thread. Adjust tone, length, and goal. Ask for a refinement in plain language. When ready, click Inject to drop the message directly into your compose field.',
    tags: ['5 deal states × 6 industries', 'Tone & length controls', 'One-click inject', 'Custom instructions', 'Refinement loop'],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
      </svg>
    ),
  },
];

const PRIVACY_POINTS = [
  {
    title: 'PII stripped client-side',
    body: 'Emails, phone numbers, SSNs, credit scores, card numbers, and street addresses are masked in your browser before any data is transmitted. The AI never sees raw personal information.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
  },
  {
    title: 'Screenshots downscaled before upload',
    body: 'Every screenshot is resized to a maximum of 1280px wide at 0.85 JPEG quality inside your browser before it leaves. This reduces AI vision processing overhead by ~60% while keeping all text legible.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
      </svg>
    ),
  },
  {
    title: 'Server-side re-masking as a second layer',
    body: 'Every edge function runs a second independent PII pass on inbound text before it reaches any AI model — defense in depth against gaps in the client-side filter.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    title: 'Analyze-only — no passive monitoring',
    body: 'Salifly only reads your conversation when you click Analyze. No keystrokes are logged, no background collection runs between sessions, and nothing is stored until you trigger an analysis.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
    ),
  },
];

export function HowItWorksPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: HowItWorksPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-wrap">
      <Nav currentPage="how-it-works" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      {/* ── Hero ── */}
      <div className="page-hero">
        <span className="page-hero-eyebrow">How it works</span>
        <h1 className="page-hero-h1">
          From full context<br />to the response that closes
        </h1>
        <p className="page-hero-sub">
          Four steps, zero configuration. Salifly reads your deal the same way you do — visually and in full context — then writes the message your lead is most likely to respond to.
        </p>
        <div className="page-hero-cta">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('features')}>See all features</button>
        </div>
      </div>

      {/* ── Steps ── */}
      <div className="page-section" style={{ paddingTop: 16, paddingBottom: 24 }}>
        <div className="hiw-steps">
          {STEPS.map((s, i) => (
            <div key={s.n} className={`hiw-step reveal reveal-delay-${(i % 3) + 1}`}>
              <div className="hiw-step-aside">
                <div className="hiw-step-num-wrap">
                  <span className="hiw-step-n">{s.n}</span>
                </div>
                <div className="hiw-step-connector" />
              </div>
              <div className="hiw-step-card">
                <div className="hiw-step-icon-wrap">
                  {s.icon}
                </div>
                <h3 className="hiw-step-h3">{s.title}</h3>
                <p className="hiw-step-body">{s.body}</p>
                <div className="hiw-step-tags">
                  {s.tags.map(t => (
                    <span key={t} className="hiw-step-tag">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Dual payload callout ── */}
      <div className="page-section" style={{ paddingTop: 0, paddingBottom: 80 }}>
        <div className="hiw-dual-payload reveal">
          <div className="hiw-dual-header">
            <span className="page-section-eyebrow" style={{ marginBottom: 12 }}>The technical edge</span>
            <h2 className="hiw-dual-h2">How Salifly defeats AI blindness</h2>
            <p className="hiw-dual-sub">
              Every other AI writing tool sends only what's visible on screen. When your lead's previous messages are off-screen, the AI hallucinates context. Salifly sends two independent data sources so the AI always has the full picture.
            </p>
          </div>
          <div className="hiw-dual-grid">
            <div className="hiw-dual-card hiw-dual-card--screenshot">
              <div className="hiw-dual-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <h4 className="hiw-dual-card-title">Screenshot</h4>
              <p className="hiw-dual-card-body">Visual context — CRM layout, lead identity anchors in headers and avatars, UI panel structure, and form fields that don't exist in the DOM text.</p>
              <ul className="hiw-dual-card-list">
                <li>Screenshot Processing</li>
                <li>Downscaled to ≤1280px before upload</li>
                <li>Spatial signals inaccessible to text-only AI</li>
              </ul>
            </div>
            <div className="hiw-dual-plus">+</div>
            <div className="hiw-dual-card hiw-dual-card--thread">
              <div className="hiw-dual-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <h4 className="hiw-dual-card-title">Full thread text</h4>
              <p className="hiw-dual-card-body">The complete conversation scraped directly from the DOM — every message, objection, buying signal, and prior commitment, no matter how far back it scrolls.</p>
              <ul className="hiw-dual-card-list">
                <li>Authoritative source for all message content</li>
                <li>Drives deal state classification</li>
                <li>Captures off-screen history the screenshot misses</li>
              </ul>
            </div>
          </div>
          <div className="hiw-dual-result">
            <span className="hiw-dual-result-label">Result</span>
            <span className="hiw-dual-result-text">Three responses calibrated to the actual conversation — not a summary of what was visible when you clicked.</span>
          </div>
        </div>
      </div>

      {/* ── Inline suggestions ── */}
      <div className="page-section" style={{ paddingTop: 0, paddingBottom: 80 }}>
        <div className="hiw-inline-callout reveal">
          <div className="hiw-inline-callout-copy">
            <span className="page-section-eyebrow" style={{ marginBottom: 12 }}>Built-in as you type</span>
            <h3 className="hiw-inline-callout-h3">Inline phrase suggestions — live in your compose field</h3>
            <p className="hiw-inline-callout-body">
              After sending a response or composing your own reply, Salifly keeps reading. It underlines weak phrases — hedging language, vague CTAs, filler words — with a purple dotted line directly inside your compose area. Click any underline to see a sharper replacement, its sales impact, and exactly why it matters for this lead.
            </p>
            <div className="hiw-inline-callout-tags">
              {['Gmail compose', 'LinkedIn reply', 'Salesforce', 'Outlook', 'Tone · Clarity · Urgency · Restructure'].map(t => (
                <span key={t} className="hiw-step-tag">{t}</span>
              ))}
            </div>
          </div>
          <div className="hiw-inline-callout-visual">
            <div className="fp-inline-mockup">
              <div className="fp-inline-compose-bar">
                <span className="fp-inline-dot" />
                <span className="fp-inline-dot" />
                <span className="fp-inline-dot" />
                <span className="fp-inline-compose-lbl">Gmail · Compose</span>
              </div>
              <p className="fp-inline-text">
                I just wanted to{' '}
                <span className="fp-sug-mark">touch base</span>
                {' '}and{' '}
                <span className="fp-sug-mark">circle back</span>
                {' '}before we{' '}
                <span className="fp-sug-mark">move forward</span>.
              </p>
              <div className="fp-inline-badge">
                <span className="fp-inline-badge-num">3</span>
                suggestions
              </div>
              <div className="fp-inline-tip">
                <div className="fp-inline-tip-row">
                  <span className="fp-inline-tip-cat">Restructure</span>
                  <span className="fp-inline-tip-original">"I just wanted to touch base and circle back before we move forward."</span>
                </div>
                <div className="fp-inline-tip-replace">→ "Still thinking about [vehicle]? I have Tuesday at 10 or Wednesday at 2."</div>
                <p className="fp-inline-tip-reason">Vague follow-up → specific time slots. Prospect sees the ask before they tune out.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Privacy ── */}
      <div className="hiw-privacy-band">
        <div className="page-section" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <span className="page-section-eyebrow reveal" style={{ display: 'block', marginBottom: 12 }}>Privacy architecture</span>
          <h2 className="page-section-h2 reveal reveal-delay-1" style={{ marginBottom: 14 }}>Two layers of privacy protection,<br />before the AI sees a word</h2>
          <p className="page-section-sub reveal reveal-delay-2">
            Regex-based PII masking runs independently on the client and on the server. If one layer misses something, the other catches it.
          </p>
          <div className="hiw-privacy-grid">
            {PRIVACY_POINTS.map((p, i) => (
              <div key={p.title} className={`hiw-privacy-card reveal reveal-delay-${(i % 2) + 1}`}>
                <div className="hiw-privacy-icon">
                  {p.icon}
                </div>
                <h4 className="hiw-privacy-title">{p.title}</h4>
                <p className="hiw-privacy-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="page-footer-cta">
        <h2>See it in action</h2>
        <p>Install in under a minute. Then let Salifly keep every deal moving with quality replies.</p>
        <div className="page-footer-cta-actions">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('pricing')}>View pricing</button>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <button className="page-footer-link" onClick={() => onNavigate('privacy' as AppPage)}>Privacy</button>
              <button className="page-footer-link" onClick={() => onNavigate('terms' as AppPage)}>Terms</button>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
